import { useState, useRef, useEffect, useCallback } from 'react';
import { consultationTypes, blueprintRituals, consultReviews } from '../../mocks/consultations';
import { SocialSidebar } from '../../components/feature/SocialSidebar';
import { useCart } from '../../context/CartContext';
import { useSEO } from '@/hooks/useSEO';

const AVAILABILITY: Record<number, string[]> = {
  1: ['13:00', '14:00', '15:00', '16:00'], // Maandag
  4: ['13:00', '14:00', '15:00', '16:00'], // Donderdag
};
const AVAILABLE_WEEK_DAYS = Object.keys(AVAILABILITY).map(Number);
const DAY_NAMES: Record<number, string> = {
  0: 'Zondag', 1: 'Maandag', 2: 'Dinsdag', 3: 'Woensdag',
  4: 'Donderdag', 5: 'Vrijdag', 6: 'Zaterdag',
};

// ── Kalender helpers ─────────────────────────────────────────────────────────
const CAL_DAYS = ['Ma', 'Di', 'Wo', 'Do', 'Vr', 'Za', 'Zo'];
const MONTHS = [
  'Januari', 'Februari', 'Maart', 'April', 'Mei', 'Juni',
  'Juli', 'Augustus', 'September', 'Oktober', 'November', 'December',
];

function isoToDate(iso: string): Date {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d);
}
function dateToIso(y: number, m: number, d: number): string {
  return `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}
function formatDisplay(iso: string): string {
  const [y, m, d] = iso.split('-');
  return `${d} ${MONTHS[parseInt(m, 10) - 1]} ${y}`;
}

// ── DatePicker ───────────────────────────────────────────────────────────────
interface DatePickerProps {
  value: string;
  onChange: (val: string) => void;
  name: string;
}

const DatePicker = ({ value, onChange, name }: DatePickerProps) => {
  const [open, setOpen] = useState(false);
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth());
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const firstDay = new Date(viewYear, viewMonth, 1);
  let startOffset = firstDay.getDay() - 1;
  if (startOffset < 0) startOffset = 6;
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();

  const prevMonth = () => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y - 1); }
    else setViewMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y + 1); }
    else setViewMonth(m => m + 1);
  };

  const isDisabled = useCallback((day: number) => {
    const d = new Date(viewYear, viewMonth, day);
    if (d < today) return true;
    return !AVAILABLE_WEEK_DAYS.includes(d.getDay());
  }, [viewYear, viewMonth, today]);

  const isSelected = useCallback((day: number) => {
    if (!value) return false;
    const sel = isoToDate(value);
    return sel.getFullYear() === viewYear && sel.getMonth() === viewMonth && sel.getDate() === day;
  }, [value, viewYear, viewMonth]);

  const isToday = (day: number) =>
    today.getFullYear() === viewYear && today.getMonth() === viewMonth && today.getDate() === day;

  const isAvailableUpcoming = (day: number) => {
    const d = new Date(viewYear, viewMonth, day);
    return d >= today && AVAILABLE_WEEK_DAYS.includes(d.getDay());
  };

  const cells: (number | null)[] = [
    ...Array(startOffset).fill(null),
    ...Array.from({ length: daysInMonth }, (_, i) => i + 1),
  ];
  while (cells.length % 7 !== 0) cells.push(null);

  const selectDay = (day: number) => {
    if (isDisabled(day)) return;
    onChange(dateToIso(viewYear, viewMonth, day));
    setOpen(false);
  };

  return (
    <div className="relative" ref={ref}>
      <input type="hidden" name={name} value={value} />
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full px-4 py-3 bg-white/5 border border-violet-500/15 rounded-xl text-sm text-left flex items-center gap-3 hover:border-violet-500/40 transition-colors cursor-pointer focus:outline-none"
      >
        <div className="w-5 h-5 flex items-center justify-center shrink-0">
          <i className="ri-calendar-line text-violet-400 text-base" />
        </div>
        <span className={value ? 'text-white' : 'text-white/25'}>
          {value ? formatDisplay(value) : 'Kies een datum'}
        </span>
        <div className="ml-auto w-4 h-4 flex items-center justify-center shrink-0">
          <i className={`ri-arrow-down-s-line text-violet-400/60 text-base transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
        </div>
      </button>

      {open && (
        <div className="absolute z-50 top-full mt-2 left-0 w-full sm:w-[320px] bg-[#120020] border border-violet-500/30 rounded-2xl p-4" style={{ boxShadow: '0 20px 60px rgba(0,0,0,0.5)' }}>
          {/* Month nav */}
          <div className="flex items-center justify-between mb-3">
            <button type="button" onClick={prevMonth} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-violet-500/15 text-violet-400 transition-colors cursor-pointer">
              <i className="ri-arrow-left-s-line text-lg" />
            </button>
            <span className="text-white font-semibold text-sm">{MONTHS[viewMonth]} {viewYear}</span>
            <button type="button" onClick={nextMonth} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-violet-500/15 text-violet-400 transition-colors cursor-pointer">
              <i className="ri-arrow-right-s-line text-lg" />
            </button>
          </div>

          {/* Beschikbaarheid hint */}
          <div className="mb-3 px-2 py-2 bg-violet-600/10 border border-violet-500/20 rounded-lg">
            <p className="text-violet-300/70 text-xs text-center">
              <i className="ri-time-line mr-1" />
              Beschikbaar op <strong className="text-violet-300">Maandag</strong> &amp; <strong className="text-violet-300">Donderdag</strong>
            </p>
          </div>

          {/* Day headers */}
          <div className="grid grid-cols-7 mb-1">
            {CAL_DAYS.map(d => (
              <div key={d} className="text-center text-violet-400/50 text-xs font-semibold py-1">{d}</div>
            ))}
          </div>

          {/* Day cells */}
          <div className="grid grid-cols-7 gap-y-0.5">
            {cells.map((day, idx) => {
              if (!day) return <div key={`e-${idx}`} className="h-9" />;
              const disabled = isDisabled(day);
              const selected = isSelected(day);
              const todayDay = isToday(day);
              const available = isAvailableUpcoming(day);

              return (
                <button
                  key={day}
                  type="button"
                  disabled={disabled}
                  onClick={() => selectDay(day)}
                  className={[
                    'h-9 w-full flex flex-col items-center justify-center rounded-lg text-xs font-medium transition-all relative',
                    disabled ? 'text-white/15 cursor-not-allowed' : 'cursor-pointer',
                    selected ? 'bg-gradient-to-br from-violet-600 to-fuchsia-600 text-white font-bold' : '',
                    !selected && available ? 'hover:bg-violet-500/20 text-white/85' : '',
                    todayDay && !selected ? 'ring-1 ring-violet-500/50 text-violet-300' : '',
                  ].join(' ')}
                >
                  <span className="leading-none">{day}</span>
                  {available && !selected && (
                    <span className="absolute bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-violet-400/70" />
                  )}
                </button>
              );
            })}
          </div>

          {/* Legend */}
          <div className="mt-3 pt-3 border-t border-violet-500/15 flex flex-wrap items-center gap-x-4 gap-y-1">
            <div className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-violet-400/70 shrink-0" />
              <span className="text-white/35 text-xs">beschikbaar</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-gradient-to-br from-violet-600 to-fuchsia-600 shrink-0" />
              <span className="text-white/35 text-xs">geselecteerd</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-white/15 shrink-0" />
              <span className="text-white/35 text-xs">niet beschikbaar</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Main page ────────────────────────────────────────────────────────────────
const ConsultationsPage = () => {
  useSEO({
    title: 'Spirituele Consultaties Den Haag & Ritueel Blueprints | ZpiritualZ',
    description: 'Boek een persoonlijk spiritueel consult bij ZpiritualZ in Den Haag. Energetische begeleiding, aura-analyse en digitale ritueel-blauwdrukken voor bescherming en transformatie. Online & op locatie beschikbaar.',
    canonical: 'https://www.zpiritualz.com/consultations',
    keywords: 'spiritueel consult Den Haag, aura reiniging consult, energetische begeleiding, ritueel blueprint, spirituele adviseur Den Haag',
    ogImage: 'https://public.readdy.ai/ai/img_res/2a1b09a31313073c65b023d44a14252d.jpg',
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'Service',
        name: 'Spirituele Consultaties',
        url: 'https://www.zpiritualz.com/consultations',
        description: 'Persoonlijke energetische begeleiding en digitale ritueel-blauwdrukken voor diepgaande spirituele transformatie.',
        provider: {
          '@type': 'Organization',
          name: 'ZpiritualZ',
          url: 'https://www.zpiritualz.com',
          address: {
            '@type': 'PostalAddress',
            streetAddress: 'Kobaltstraat 21',
            addressLocality: 'Den Haag',
            postalCode: '2544 ET',
            addressCountry: 'NL',
          },
        },
        areaServed: { '@type': 'Country', name: 'Netherlands' },
        serviceType: 'Spirituele Begeleiding',
        offers: [
          { '@type': 'Offer', name: 'Spiritueel Consult 30 min', price: '45.00', priceCurrency: 'EUR' },
          { '@type': 'Offer', name: 'Spiritueel Consult 60 min', price: '75.00', priceCurrency: 'EUR' },
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name: 'Hoe boek ik een spiritueel consult bij ZpiritualZ?',
            acceptedAnswer: { '@type': 'Answer', text: 'Kies een consult type op de consultatiepagina, vul het formulier in met je naam, e-mail en gewenste datum (maandag of donderdag), en betaal veilig via Stripe. Je ontvangt een bevestiging per e-mail.' },
          },
          {
            '@type': 'Question',
            name: 'Wat zijn de beschikbare tijden voor een consult?',
            acceptedAnswer: { '@type': 'Answer', text: 'Consultaties zijn beschikbaar op maandag en donderdag tussen 13:00 en 16:00 uur.' },
          },
          {
            '@type': 'Question',
            name: 'Wat zijn Ritueel Blueprints?',
            acceptedAnswer: { '@type': 'Answer', text: 'Ritueel Blueprints zijn complete digitale gidsen voor spirituele reinigingsrituelen. Na aankoop ontvang je direct een downloadlink per e-mail. De blueprints zijn in PDF-formaat en levenslang toegankelijk.' },
          },
        ],
      },
    ],
  });

  const [selected, setSelected] = useState<string | null>(null);
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [formStatus, setFormStatus] = useState<'idle' | 'sending' | 'error'>('idle');

  const initParams = new URLSearchParams(window.location.search);
  const [activeTab, setActiveTab] = useState<'consult' | 'blueprints'>(
    initParams.get('tab') === 'blueprints' ? 'blueprints' : 'consult'
  );

  // Compute available timeslots based on selected date
  const availableSlots: string[] = selectedDate
    ? AVAILABILITY[isoToDate(selectedDate).getDay()] ?? []
    : [];

  const selectedDayName: string | null = selectedDate
    ? DAY_NAMES[isoToDate(selectedDate).getDay()] ?? null
    : null;

  // Reset time when date changes
  const handleDateChange = (val: string) => {
    setSelectedDate(val);
    setSelectedTime(null);
  };

  const { addToCart } = useCart();

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!selected) return;
    const form = e.target as HTMLFormElement;
    const data = new FormData(form);
    const firstName = data.get('first_name') as string;
    const lastName = data.get('last_name') as string;
    const email = data.get('email') as string;
    const phone = data.get('phone') as string;
    const message = data.get('message') as string;
    const selectedType = consultationTypes.find(t => t.id === selected);
    addToCart({
      id: `consult-${selected}-${Date.now()}`,
      name: selectedType?.title ?? 'Spiritueel Consult',
      price: selectedType?.price ?? 0,
      type: 'consultation',
      description: selectedType?.description ?? '',
      metadata: {
        consultation_type: selected,
        name: `${firstName} ${lastName}`,
        email,
        phone,
        date: selectedDate,
        time: selectedTime ?? '',
        message,
      },
    });
  };

  const selectedType = consultationTypes.find(t => t.id === selected);
  const urlParams = new URLSearchParams(window.location.search);
  const paymentStatus = urlParams.get('status');

  return (
    <div className="min-h-screen bg-[#050010] pt-20">
      <SocialSidebar />

      {/* Hero */}
      <div className="relative py-20 text-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=spiritual%20consultation%20healing%20session%20mystical%20divine%20energy%20guidance%20dark%20cosmic%20purple%20violet%20background%20sacred%20ceremony%20spiritual%20advisor%20crystals%20ethereal%20atmosphere%20divine%20feminine%20guidance&width=1920&height=600&seq=conshero2&orientation=landscape"
            alt="Consultaties"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#050010]/80 via-[#050010]/65 to-[#050010]" />
        </div>
        <div className="relative z-10 px-6">
          <p className="text-violet-400 text-xs font-semibold uppercase tracking-widest mb-3">✦ Persoonlijke Begeleiding ✦</p>
          <h1 className="text-5xl font-black text-white mb-4">
            Spirituele <span className="text-transparent bg-gradient-to-r from-violet-400 to-fuchsia-400 bg-clip-text">Consultaties</span>
          </h1>
          <p className="text-white/55 text-base max-w-2xl mx-auto">
            Persoonlijke energetische begeleiding en digitale ritueel-blauwdrukken voor diepgaande spirituele transformatie.
          </p>
        </div>
      </div>

      <div className="max-w-[1440px] mx-auto px-6 lg:px-12 py-10">
        {/* Tab Switcher */}
        <div className="flex justify-center mb-12">
          <div className="inline-flex items-center gap-1 p-1 bg-[#110020] border border-violet-500/20 rounded-full">
            <button
              onClick={() => setActiveTab('consult')}
              className={`px-6 py-2.5 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                activeTab === 'consult'
                  ? 'bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white'
                  : 'text-white/50 hover:text-white'
              }`}
            >
              <i className="ri-calendar-line mr-2" />
              Consult Boeken
            </button>
            <button
              onClick={() => setActiveTab('blueprints')}
              className={`px-6 py-2.5 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                activeTab === 'blueprints'
                  ? 'bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white'
                  : 'text-white/50 hover:text-white'
              }`}
            >
              <i className="ri-file-download-line mr-2" />
              Ritueel Blueprints
            </button>
          </div>
        </div>

        {/* ── CONSULT TAB ──────────────────────────────────────────── */}
        {activeTab === 'consult' && (
          <>
            {/* Consult Types */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-8 text-center">Kies Jouw Consult</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto">
                {consultationTypes.map(type => (
                  <button
                    key={type.id}
                    onClick={() => setSelected(type.id)}
                    className={`text-left p-6 rounded-2xl border transition-all duration-300 cursor-pointer relative ${
                      selected === type.id
                        ? 'border-violet-500/60 bg-gradient-to-br from-violet-600/15 to-fuchsia-600/15'
                        : 'border-violet-500/15 bg-[#110020]/60 hover:border-violet-500/35 hover:bg-[#110020]'
                    }`}
                  >
                    {type.popular && (
                      <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white text-xs font-bold rounded-full whitespace-nowrap">
                        Meest Gekozen
                      </span>
                    )}
                    <div className="flex items-start justify-between mb-3">
                      <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-violet-600/20">
                        <i className={`${type.icon} text-xl text-violet-400`} />
                      </div>
                      {selected === type.id && (
                        <div className="w-6 h-6 flex items-center justify-center rounded-full bg-violet-500">
                          <i className="ri-check-line text-xs text-white" />
                        </div>
                      )}
                    </div>
                    <h3 className="text-white font-bold text-lg mb-0.5">{type.title}</h3>
                    <p className="text-violet-400 text-xs font-medium mb-1">{type.subtitle}</p>
                    <p className="text-white/40 text-xs mb-1">
                      <i className="ri-time-line mr-1" />{type.duration}
                    </p>
                    <p className="text-white/50 text-xs leading-relaxed mb-4">{type.description}</p>
                    <div className="space-y-1.5 mb-5">
                      {type.includes.map(item => (
                        <div key={item} className="flex items-start gap-2">
                          <i className="ri-check-line text-violet-400 text-xs mt-0.5 shrink-0" />
                          <span className="text-white/45 text-xs">{item}</span>
                        </div>
                      ))}
                    </div>
                    <div className="pt-4 border-t border-violet-500/10">
                      <span className="text-white font-black text-2xl">&euro;{type.price}</span>
                      <span className="text-white/30 text-xs ml-2">eenmalig</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Booking Form */}
            <div className="max-w-3xl mx-auto">
              <div className="bg-[#110020]/60 border border-violet-500/15 rounded-3xl p-8">
                <h2 className="text-xl font-bold text-white mb-2 text-center">Afspraak &amp; Betaling</h2>
                {selectedType && (
                  <p className="text-center text-violet-400 text-sm mb-6">
                    Geselecteerd: <strong>{selectedType.title}</strong> – &euro;{selectedType.price}
                  </p>
                )}
                {!selected && (
                  <p className="text-center text-white/30 text-sm mb-6">Selecteer hierboven een consult type</p>
                )}

                {paymentStatus === 'success' ? (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 flex items-center justify-center rounded-full bg-violet-500/20 mx-auto mb-4">
                      <i className="ri-check-double-line text-3xl text-violet-400" />
                    </div>
                    <h3 className="text-white font-bold text-xl mb-2">Betaling Geslaagd!</h3>
                    <p className="text-white/45 text-sm max-w-xs mx-auto">Je ontvangt binnen een paar minuten een bevestiging per e-mail. We nemen contact op om de afspraak in te plannen.</p>
                    <button
                      onClick={() => window.history.replaceState({}, '', window.location.pathname)}
                      className="mt-6 px-6 py-3 border border-violet-500/30 text-violet-400 rounded-xl text-sm hover:bg-violet-500/10 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      Terug naar Consultaties
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} data-readdy-form id="consultation-booking-form" className="space-y-5">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-white/50 text-xs mb-2">Voornaam *</label>
                        <input name="first_name" type="text" required className="w-full px-4 py-3 bg-white/5 border border-violet-500/15 rounded-xl text-sm text-white placeholder-white/25 focus:outline-none focus:border-violet-500/50 transition-colors" placeholder="Luna" />
                      </div>
                      <div>
                        <label className="block text-white/50 text-xs mb-2">Achternaam *</label>
                        <input name="last_name" type="text" required className="w-full px-4 py-3 bg-white/5 border border-violet-500/15 rounded-xl text-sm text-white placeholder-white/25 focus:outline-none focus:border-violet-500/50 transition-colors" placeholder="de Vries" />
                      </div>
                    </div>

                    <div>
                      <label className="block text-white/50 text-xs mb-2">E-mailadres *</label>
                      <input name="email" type="email" required className="w-full px-4 py-3 bg-white/5 border border-violet-500/15 rounded-xl text-sm text-white placeholder-white/25 focus:outline-none focus:border-violet-500/50 transition-colors" placeholder="jij@email.nl" />
                    </div>

                    <div>
                      <label className="block text-white/50 text-xs mb-2">Telefoonnummer</label>
                      <input name="phone" type="tel" className="w-full px-4 py-3 bg-white/5 border border-violet-500/15 rounded-xl text-sm text-white placeholder-white/25 focus:outline-none focus:border-violet-500/50 transition-colors" placeholder="+31 6 12345678" />
                    </div>

                    {/* Datum picker */}
                    <div>
                      <label className="block text-white/50 text-xs mb-2">Gewenste datum</label>
                      <DatePicker name="date" value={selectedDate} onChange={handleDateChange} />
                    </div>

                    {/* Tijdslots — dynamisch op basis van datum */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className="text-white/50 text-xs">Tijdvoorkeur</label>
                        {selectedDate && selectedDayName && (
                          <span className="text-violet-400/70 text-xs">
                            <i className="ri-calendar-check-line mr-1" />
                            {selectedDayName} {formatDisplay(selectedDate).split(' ').slice(0, 2).join(' ')}
                          </span>
                        )}
                      </div>

                      {!selectedDate && (
                        <div className="flex items-center gap-2 px-4 py-3 bg-white/3 border border-white/8 rounded-xl">
                          <i className="ri-calendar-line text-white/25 text-sm" />
                          <p className="text-white/30 text-xs">Kies eerst een datum om beschikbare tijden te zien</p>
                        </div>
                      )}

                      {selectedDate && availableSlots.length === 0 && (
                        <div className="flex items-center gap-3 px-4 py-3 bg-rose-500/8 border border-rose-500/20 rounded-xl">
                          <div className="w-5 h-5 flex items-center justify-center shrink-0">
                            <i className="ri-close-circle-line text-rose-400 text-sm" />
                          </div>
                          <div>
                            <p className="text-rose-300/80 text-xs font-medium">Geen beschikbaarheid</p>
                            <p className="text-rose-300/50 text-xs">Op deze dag zijn er geen consultaties. Kies een maandag of donderdag.</p>
                          </div>
                        </div>
                      )}

                      {selectedDate && availableSlots.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                          {availableSlots.map(t => (
                            <button
                              key={t}
                              type="button"
                              onClick={() => setSelectedTime(t)}
                              className={`px-4 py-2.5 rounded-lg text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                                selectedTime === t
                                  ? 'bg-gradient-to-r from-violet-600/40 to-fuchsia-600/40 border border-violet-500/60 text-violet-200'
                                  : 'bg-white/5 border border-white/10 text-white/50 hover:border-violet-500/40 hover:text-white/80'
                              }`}
                            >
                              <i className={`ri-time-line mr-1.5 ${selectedTime === t ? 'text-violet-300' : 'text-white/30'}`} />
                              {t}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>

                    <div>
                      <label className="block text-white/50 text-xs mb-2">Jouw vraag of situatie</label>
                      <textarea
                        name="message"
                        rows={3}
                        maxLength={500}
                        className="w-full px-4 py-3 bg-white/5 border border-violet-500/15 rounded-xl text-sm text-white placeholder-white/25 focus:outline-none focus:border-violet-500/50 transition-colors resize-none"
                        placeholder="Vertel kort wat je hoopt te ontdekken of bereiken..."
                      />
                    </div>

                    {formStatus === 'error' && (
                      <p className="text-rose-400 text-sm text-center">Er is iets misgegaan. Probeer opnieuw.</p>
                    )}

                    <button
                      type="submit"
                      disabled={!selected}
                      className="w-full py-4 bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white font-bold rounded-xl whitespace-nowrap hover:opacity-90 transition-opacity cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      <i className="ri-shopping-bag-line text-sm" />
                      {`In Wagen Plaatsen${selectedType ? ` — \u20AC${selectedType.price}` : ''}`}
                    </button>
                    <p className="text-center text-white/25 text-xs">
                      <i className="ri-shield-check-line mr-1" />
                      Afrekenen via winkelwagen &middot; veilig betalen met Stripe
                    </p>
                  </form>
                )}
              </div>
            </div>
          </>
        )}

        {/* ── BLUEPRINTS TAB ─────────────────────────────────────────── */}
        {activeTab === 'blueprints' && (
          <div>
            {paymentStatus === 'success' && (
              <div className="max-w-4xl mx-auto mb-8 p-5 bg-violet-600/15 border border-violet-500/40 rounded-2xl flex items-center gap-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-violet-500/25 shrink-0">
                  <i className="ri-check-double-line text-violet-400 text-lg" />
                </div>
                <div>
                  <p className="text-white font-semibold text-sm">Betaling geslaagd!</p>
                  <p className="text-white/50 text-xs">Je downloadlink is verstuurd naar je e-mailadres. Check ook je spam-folder.</p>
                </div>
                <button onClick={() => window.history.replaceState({}, '', window.location.pathname)} className="ml-auto w-7 h-7 flex items-center justify-center text-white/30 hover:text-white/60 transition-colors cursor-pointer">
                  <i className="ri-close-line" />
                </button>
              </div>
            )}
            <div className="text-center mb-10">
              <p className="text-violet-400 text-xs font-semibold uppercase tracking-widest mb-3">✦ Digitale Gidsen ✦</p>
              <h2 className="text-3xl font-bold text-white mb-3">Ritueel Blueprints</h2>
              <p className="text-white/45 text-sm max-w-xl mx-auto">
                Complete digitale gidsen voor spirituele reinigingsrituelen. Direct te downloaden na aankoop via Stripe.
              </p>
            </div>

            <div className="max-w-4xl mx-auto mb-8">
              <div className="flex items-center gap-3 p-4 bg-[#1a0030]/60 border border-violet-500/20 rounded-xl">
                <div className="w-8 h-8 flex items-center justify-center shrink-0">
                  <i className="ri-shield-check-line text-violet-400 text-lg" />
                </div>
                <p className="text-white/60 text-xs flex-1">
                  <strong className="text-violet-400">Veilig betalen met Stripe</strong> — Na betaling ontvang je direct een downloadlink per e-mail. Beschikbaar via creditcard en iDEAL.
                </p>
                <div className="flex items-center gap-2 shrink-0">
                  <i className="ri-lock-line text-violet-400/60 text-sm" />
                  <span className="text-white/35 text-xs">256-bit SSL</span>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
              {blueprintRituals.map(ritual => (
                <div
                  key={ritual.id}
                  className={`group bg-gradient-to-br ${ritual.color} border border-violet-500/20 rounded-2xl overflow-hidden hover:border-violet-500/45 transition-all duration-300`}
                >
                  <div className="relative h-44 overflow-hidden">
                    <img src={ritual.image} alt={ritual.title} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#050010]/90 via-transparent to-transparent" />
                    <div className="absolute top-3 right-3 w-9 h-9 flex items-center justify-center rounded-full bg-[#050010]/60 backdrop-blur-sm border border-violet-500/20">
                      <i className={`${ritual.icon} text-violet-400 text-base`} />
                    </div>
                  </div>
                  <div className="p-5">
                    <p className="text-violet-400/70 text-xs uppercase tracking-wide mb-1">Digitale Gids</p>
                    <h3 className="text-white font-bold text-base mb-1">{ritual.title}</h3>
                    <p className="text-fuchsia-400/60 text-xs mb-3">{ritual.subtitle}</p>
                    <p className="text-white/45 text-xs leading-relaxed mb-4">{ritual.description}</p>
                    <div className="space-y-1.5 mb-5">
                      {ritual.includes.slice(0, 4).map(item => (
                        <div key={item} className="flex items-start gap-2">
                          <i className="ri-check-line text-violet-400 text-xs mt-0.5 shrink-0" />
                          <span className="text-white/40 text-xs">{item}</span>
                        </div>
                      ))}
                      {ritual.includes.length > 4 && (
                        <p className="text-violet-400/50 text-xs pl-4">+{ritual.includes.length - 4} meer inbegrepen</p>
                      )}
                    </div>
                    <div className="flex items-center justify-between pt-4 border-t border-violet-500/10">
                      <div>
                        <span className="text-white font-black text-xl">&euro;{ritual.price.toFixed(2)}</span>
                        <span className="text-white/25 text-xs ml-1.5">eenmalig</span>
                      </div>
                      <BlueprintOrderButton ritual={ritual} />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="max-w-5xl mx-auto mt-8 p-6 bg-[#110020]/50 border border-violet-500/15 rounded-2xl">
              <div className="flex flex-col sm:flex-row items-center gap-4 text-center sm:text-left">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-violet-600/20 shrink-0">
                  <i className="ri-download-cloud-line text-violet-400 text-xl" />
                </div>
                <div>
                  <h4 className="text-white font-semibold text-sm mb-1">Direct Downloaden na Betaling</h4>
                  <p className="text-white/40 text-xs">Na succesvolle betaling via Stripe ontvang je direct een e-mail met je downloadlink. De blueprints zijn in PDF-formaat en levenslang toegankelijk.</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── REVIEWS ─────────────────────────────────────────── */}
      <section className="py-24 bg-gradient-to-b from-[#0a0014] to-[#050010]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="text-center mb-14">
            <p className="text-violet-400 text-xs font-semibold uppercase tracking-widest mb-4">✦ Ervaringen ✦</p>
            <h2 className="text-4xl font-bold text-white mb-4">
              Wat Klanten{' '}
              <span className="text-transparent bg-gradient-to-r from-violet-400 to-fuchsia-400 bg-clip-text">Zeggen</span>
            </h2>
            <p className="text-white/45 text-sm max-w-xl mx-auto mb-10">
              Echte ervaringen van mensen die de stap zetten naar een consult. Geen scripts, geen filters.
            </p>
            <div className="inline-flex flex-wrap items-center justify-center gap-8 px-10 py-6 bg-violet-600/8 border border-violet-500/20 rounded-2xl">
              <div className="text-center">
                <p className="text-5xl font-black text-white mb-1">5.0</p>
                <div className="flex items-center justify-center gap-0.5 mb-1">
                  {[...Array(5)].map((_, i) => <i key={i} className="ri-star-fill text-[#d4a017] text-sm" />)}
                </div>
                <p className="text-white/35 text-xs">Gemiddelde beoordeling</p>
              </div>
              <div className="h-12 w-px bg-white/8 hidden sm:block" />
              <div className="text-center">
                <p className="text-5xl font-black text-white mb-1">48</p>
                <p className="text-white/35 text-xs mt-1">Beoordelingen</p>
              </div>
              <div className="h-12 w-px bg-white/8 hidden sm:block" />
              <div className="text-center">
                <p className="text-5xl font-black text-white mb-1">100%</p>
                <p className="text-white/35 text-xs mt-1">Zou het aanraden</p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {consultReviews.map((review, i) => (
              <div
                key={review.id}
                className={`relative p-6 rounded-2xl border border-violet-500/15 bg-white/2 hover:border-violet-500/30 transition-all duration-300 flex flex-col gap-4 ${i === 0 ? 'lg:col-span-1 border-violet-500/30 bg-violet-600/5' : ''}`}
              >
                <div className="flex items-center gap-0.5">
                  {[...Array(review.rating)].map((_, s) => <i key={s} className="ri-star-fill text-[#d4a017] text-sm" />)}
                </div>
                <p className="text-white/65 text-sm leading-relaxed flex-1">&ldquo;{review.message}&rdquo;</p>
                <div className="inline-flex items-center gap-1.5 self-start px-3 py-1 bg-violet-600/15 border border-violet-500/25 rounded-full">
                  <i className="ri-radar-line text-violet-400 text-xs" />
                  <span className="text-violet-300/80 text-xs font-medium">{review.consultType}</span>
                </div>
                <div className="flex items-center gap-3 pt-3 border-t border-white/6">
                  <div className="w-10 h-10 rounded-full overflow-hidden shrink-0 border border-violet-500/20">
                    <img src={review.avatar} alt={review.name} className="w-full h-full object-cover object-top" />
                  </div>
                  <div>
                    <p className="text-white font-semibold text-sm">{review.name}</p>
                    <p className="text-white/35 text-xs">{review.location} · {review.date}</p>
                  </div>
                  <div className="ml-auto w-6 h-6 flex items-center justify-center rounded-full bg-violet-500/20 shrink-0">
                    <i className="ri-check-line text-violet-400 text-xs" />
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-14">
            <p className="text-white/40 text-sm mb-6">Klaar om jouw eigen ervaring te creëren?</p>
            <button
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white font-bold rounded-full whitespace-nowrap hover:opacity-90 transition-opacity cursor-pointer"
            >
              <i className="ri-calendar-line" />
              Boek Jouw Consult
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

// ── Blueprint Order Button ────────────────────────────────────────────────────
interface BlueprintOrderButtonProps {
  ritual: { id: string; title: string; price: number; image: string };
}

const BlueprintOrderButton = ({ ritual }: BlueprintOrderButtonProps) => {
  const { addToCart } = useCart();
  const [added, setAdded] = useState(false);

  const handleAdd = () => {
    addToCart({
      id: `blueprint-ritual-${ritual.id}`,
      name: `Blueprint: ${ritual.title}`,
      price: ritual.price,
      type: 'blueprint',
      image: ritual.image,
      description: 'Digitale ritueel blueprint — direct per e-mail bezorgd na aankoop.',
    });
    setAdded(true);
    setTimeout(() => setAdded(false), 2000);
  };

  return (
    <button
      onClick={handleAdd}
      className={`flex items-center gap-1.5 px-4 py-2 text-xs font-semibold rounded-xl whitespace-nowrap transition-all duration-300 cursor-pointer ${
        added
          ? 'bg-emerald-600 text-white'
          : 'bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white hover:opacity-90'
      }`}
    >
      {added ? (
        <><i className="ri-check-line" />In wagen!</>
      ) : (
        <><i className="ri-shopping-bag-line" />In Wagen</>
      )}
    </button>
  );
};

export default ConsultationsPage;
